import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../../contexts/ObsContext';
import { Btn } from '../../components/ui';
import { toast } from 'react-toastify';
import { debriefAPI } from '../../api';
import useRequireObs from '../../lib/useRequireObs';
import { BUCKETS, WHO_OPTIONS } from '../../theme';

const WHO_LABEL = Object.fromEntries(WHO_OPTIONS.map(o => [o.value, o.label]));

/**
 * The opening. It opens with thanks rather than a question, so the teacher is
 * not put on the spot before they have heard anything. It names that being
 * watched is hard, says plainly that the observer was looking at students
 * rather than at them, and confirms nothing goes to anyone else.
 */
const OPENING =
  'Thank you for having me in your room today. I know it is not a small thing to have ' +
  'someone watching while you teach. I was not there to look at you, I was there to look ' +
  'at what the students were doing, so everything I am about to share is about them. ' +
  'Nothing here goes to anyone else.';

/** The close offers a conversation rather than requiring one. */
const CLOSING =
  'Again, thank you for having me in your room. If you have any questions about any of ' +
  'my notes around student learning, please let me know.';

/** Said only if they do want to talk — and it starts with the students. */
const IF_QUESTIONS =
  'Which of those students would you like to talk about? Start wherever you want.';

const OUTCOMES = [
  { key: 'now',   label: 'They had questions and we talked them through' },
  { key: 'later', label: 'They may come back with questions later' },
  { key: 'none',  label: 'No questions, they took the notes as they were' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function DebriefPage() {
  const navigate = useNavigate();
  const { obs, teacher, finalBest, finalBetter, nextStep, setNextStep, setCloseOutcome } = useObs();
  useRequireObs();
  const [outcome, setOutcome] = useState(null);
  const [loading, setLoading] = useState(false);

  const teacherName = teacher?.display_name || teacher?.full_name || 'the teacher';

  // The selected ELO statements, word for word, in the order they will be
  // said. Nothing is paraphrased between the summary page and here.
  const said = [...finalBest, ...finalBetter];

  const subtitle = [
    obs?.school_name,
    obs?.grade_level,
    obs?.subject,
    formatDate(obs?.observation_date),
  ].filter(Boolean).join(' · ');

  const handleSave = async () => {
    if (!outcome) {
      toast.error('Record where the conversation went.');
      return;
    }
    setLoading(true);
    try {
      await debriefAPI.create({
        observation_id:   obs?.id,
        mode_used:        'script',
        close_outcome:    outcome,
        agreed_next_step: outcome === 'now' ? nextStep : '',
        leverage_best:    finalBest,
        leverage_better:  finalBetter,
      });
      setCloseOutcome(outcome);
      toast.success('Debrief saved.');
      navigate('/observe/feedback');
    } catch (error) {
      console.error(error);
      toast.error('Could not save the debrief. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/observe/leverage')}>← Back</button>
        <div className="obsbar-title">Debrief — step 2 of 3</div>
      </div>

      <div className="scroll">
        <div className="pad">
          {/* Who this debrief is with */}
          <div className="db-head">
            <div className="db-avatars">
              <span className="db-av db-av-obs">EW</span>
              <span className="db-arrow">→</span>
              <span className="db-av db-av-tch">{teacher?.initials || '—'}</span>
            </div>
            <div>
              <div className="db-head-name">Debrief with {teacherName}</div>
              <div className="db-head-sub">{subtitle}</div>
            </div>
          </div>

          {/* Open */}
          <div className="db-block db-open">
            <div className="db-block-lbl">Open — thank them first</div>
            <p>“{OPENING}”</p>
          </div>

          {/* The statements, word for word */}
          <div className="db-sec">Read these verbatim</div>

          {said.length === 0 ? (
            <div className="db-empty">
              No findings were selected. Go back and choose what to share.
            </div>
          ) : said.map((card, i) => {
            const neg = card.side === 'neg';
            const bucket = BUCKETS[card.bucket];
            return (
              <div key={card.id || i} className={`db-stmt ${neg ? 'neg' : 'pos'}`}>
                <div className="db-stmt-top">
                  <span className="db-num">{i + 1}</span>
                  <span className="db-kind">{neg ? 'Opportunity' : 'Strength'}</span>
                  {bucket && <span className="db-bucket">{bucket.name}</span>}
                </div>
                <p className="db-stmt-body">{card.statement}</p>
                <div className="db-stmt-foot">
                  ✓ Read verbatim{card.studentId
                    ? ` · ${card.studentId}`
                    : WHO_LABEL[card.who] ? ` · ${WHO_LABEL[card.who]}` : ''}
                </div>
              </div>
            );
          })}

          {/* Close */}
          <div className="db-block db-close">
            <div className="db-block-lbl">Close — thank them, leave the door open</div>
            <p>“{CLOSING}”</p>
          </div>

          {/* Where it went */}
          <div className="db-sec">Where did the conversation go?</div>
          <div className="db-outcomes">
            {OUTCOMES.map(o => (
              <button
                key={o.key}
                className={`db-outcome${outcome === o.key ? ' on' : ''}`}
                onClick={() => setOutcome(o.key)}
              >
                {o.label}
              </button>
            ))}
          </div>

          {/* Only if they wanted to talk — and it starts with the students. */}
          {outcome === 'now' && (
            <>
              <div className="db-block db-questions">
                <div className="db-block-lbl">If they have questions — start with the students</div>
                <p>“{IF_QUESTIONS}”</p>
              </div>

              <div className="db-agreed">
                <div className="db-agreed-lbl">Anything agreed for those students</div>
                <textarea
                  className="fi"
                  rows={3}
                  value={nextStep}
                  onChange={e => setNextStep(e.target.value)}
                  placeholder="Optional — only if something was actually agreed."
                />
              </div>
            </>
          )}

          <Btn variant="blue" onClick={handleSave} loading={loading}
               style={{ marginTop: 16, marginBottom: 24 }}>
            Save debrief &amp; generate feedback →
          </Btn>
        </div>
      </div>
    </div>
  );
}
